// Zone Finder ("Action Map" in the UI) — ported from js/renderers-zonefinder.js
// (renderZoneFinderModule). EXPERIMENTAL + ring-fenced, like the legacy: its own opt-in data
// file (useZoneFinderData), its own fixed season. needsPlayer:false — a database-wide
// "who does X most in this part of the pitch" query. The pure logic is zonefinder-view.ts;
// this paints it with the legacy's exact DOM + classes.
//
// Reads its `_`-state every render (no rebuildOnRefresh), so Undo / presets / the header
// "All" show what was saved — and, like the legacy's persistent DOM, the component itself
// survives a refresh (the empty-message quirk below depends on that).
//
// Hover = one mousemove listener doing the nearest-dot hit-test (as the legacy); the dots
// themselves are JSX. The zone drag is useZoneDraw.

import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainZoneFinder } from "./explain.js";
import { LIVE } from "../live/flag.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { EventDetailLayout } from "./EventDetailParts.js";
import { PitchMarkings } from "./PitchMarkings.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { EventFilterBar } from "./EventFilterBar.js";
import { ValuePicker, popoverPosUnder, type PopoverPos } from "./FilterPopovers.js";
import { useSlotPicker } from "./picker-slot.js";
import { useZoneDraw } from "./useZoneDraw.js";
import {
  ZONE_EXPERIMENTAL_TEXT,
  ZONE_LEAGUE_OPTIONS,
  ZONE_RANGE_FILTERS,
  ZONE_YEAR_CODE,
  buildZoneMatchInfo,
  formatCount,
  nearestZoneDot,
  resolveZoneActionKey,
  zoneActionKeyForLabel,
  zoneActionTypes,
  zoneDotTooltip,
  zoneDots,
  zoneEmptyText,
  zoneEntries,
  zoneLeaderboard,
  zoneLoadPrompt,
  zonePlayersById,
  zonePlottable,
  zonePopulation,
  zoneRefPlayer,
  zoneRowMeta,
  zoneWhatOptions,
  zoneYearOptions,
  type ZoneEntry,
  type ZoneLoadState,
  type ZonePlayer,
} from "./zonefinder-view.js";
import { pitchZoomView, pruneStaleEventFilters, type EventActive, type EventArea } from "../engines/event-map-kit.js";
import type { ActiveFilters, KitPlayer } from "../engines/stats-filter-kit.js";
import { readSnapshot } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { useEventContext } from "../hooks/useEventContext.js";
import { useFilterOptions } from "../hooks/useFilterOptions.js";
import { useZoneFinderData } from "../hooks/useZoneFinderData.js";
import { getStickyPlayer, setStickyPlayer } from "../canvas/sticky-player.js";
import { playerIdOf } from "../data/players.js";
import { startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";

// Fixed one-option pickers (the module is pinned to one season) — module-level so
// StyledSelect doesn't rebuild the legacy dropdown on every render.
const LEAGUE_ITEMS: StyledSelectItem[] = ZONE_LEAGUE_OPTIONS.map((o) => ({ ...o }));
const YEAR_ITEMS: StyledSelectItem[] = zoneYearOptions();
const noop = () => {};
const NO_REF: KitPlayer = {};
const HIDDEN = { display: "none" } as const;

// What the legacy leaves in the hidden `.event-load-prompt-host` — see the empty-message
// quirk in ZoneFinderModule.
type HostContent = { kind: "prompt"; state: ZoneLoadState } | { kind: "text"; text: string };

export function ZoneFinderModule({ mod, body }: ProtoModuleProps) {
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const svgRef = useRef<SVGSVGElement>(null);
  const ctx = useEventContext();
  const types = useMemo(() => zoneActionTypes(ctx), [ctx]);
  const filterOptions = useFilterOptions();
  const { data, load } = useZoneFinderData();
  const players = readSnapshot().players as Record<string, ZonePlayer>;
  const byId = useMemo(() => zonePlayersById(players), [players]);
  const matchInfo = useMemo(() => (data ? buildZoneMatchInfo(data) : null), [data]);

  // --- `_`-state, seeded as the legacy does (before its load gate) ---
  const typeKey = resolveZoneActionKey(getModState<string>(mod, "_zoneEventType"));
  setModState(mod, "_zoneEventType", typeKey);
  const type = types[typeKey];
  if (!getModState(mod, "_zoneBioFilters")) setModState(mod, "_zoneBioFilters", {});
  if (!getModState(mod, "_zoneEventFilters")) setModState(mod, "_zoneEventFilters", {});
  const bioActive = getModState<ActiveFilters>(mod, "_zoneBioFilters")!;
  const eventActive = getModState<EventActive>(mod, "_zoneEventFilters")!;
  const zone = getModState<EventArea | null>(mod, "_zoneArea") || null;
  const loaded = !!data;
  // The legacy seeds the search only once the data is in (its unloaded branch returns first).
  if (loaded && getModState(mod, "_zoneNameSearch") === undefined) setModState(mod, "_zoneNameSearch", "");
  const nameSearch = getModState<string>(mod, "_zoneNameSearch") ?? "";

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  const setZone = (next: EventArea | null) => {
    setModState(mod, "_zoneArea", next);
    rerender();
  };
  useZoneDraw(svgRef, {
    onZone: setZone,
    // A plain click clears the zone — a quick alternative to the "Clear zone" button.
    onPlainClick: () => {
      if (getModState(mod, "_zoneArea")) setZone(null);
    },
  });

  // --- the population (only once loaded) ---
  const entries = useMemo(() => (data ? zoneEntries(zonePopulation(data, byId, type)) : []), [data, byId, type]);
  const unionEvents = useMemo(() => entries.map((en) => en.event), [entries]);
  // Self-heal the How chips against this action type's events, in place, every render.
  if (loaded) pruneStaleEventFilters(eventActive, unionEvents, type.filterDefs);
  const refPlayer = zoneRefPlayer(players, getStickyPlayer());
  const query = { filterDefs: type.filterDefs, eventActive, bioActive, refPlayer, nameSearch };
  const dots = loaded ? zoneDots(zonePlottable(entries, query), zone) : { dots: [] as ZoneEntry[], radius: 0, hitRadius: 0 };
  const ranked = loaded ? zoneLeaderboard(entries, zone, query) : [];

  // --- dot tooltips: one listener, wired once, reading the latest dots (as the legacy) ---
  const latest = useRef({ dots, type, matchInfo });
  latest.current = { dots, type, matchInfo };
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onMove = (e: MouseEvent) => {
      // A zone drag in progress owns the pointer.
      if (svg.dataset.zoneDragging === "1") return;
      const { dots: d, type: t, matchInfo: info } = latest.current;
      if (!d.dots.length || !info) {
        hideTooltip();
        return;
      }
      const ctm = (svg.querySelector<SVGGElement>(".pitch-rotate") ?? svg).getScreenCTM();
      if (!ctm) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const hit = nearestZoneDot(d.dots, pt.matrixTransform(ctm.inverse()), d.hitRadius);
      if (hit) showTooltipAt(zoneDotTooltip(t, hit, info), e.clientX, e.clientY, "below");
      else hideTooltip();
    };
    const onLeave = () => hideTooltip();
    svg.addEventListener("mousemove", onMove);
    svg.addEventListener("mouseleave", onLeave);
    return () => {
      svg.removeEventListener("mousemove", onMove);
      svg.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // --- the empty-message quirk (kept 1:1, Prateek 2026-09-24; logged for Ben) ---
  // The legacy looks up "the" `.stats-empty` with wrap.querySelector — which finds the load
  // prompt's own `.stats-empty.event-load-prompt` first once the prompt has been drawn. So in
  // a widget that has shown the prompt, the "No real …" message is written INTO the (hidden)
  // prompt box and never seen; the real one below the pitch only works in a widget opened
  // after the data loaded. These refs mirror what that leaves in each box.
  const promptShown = useRef(false);
  const host = useRef<{ content: HostContent; display: "" | "none" }>({ content: { kind: "prompt", state: undefined }, display: "" });
  const sibling = useRef<{ text: string; display: "" | "none" }>({ text: "", display: "none" });
  const emptyText = loaded && ranked.length < 1 ? zoneEmptyText(nameSearch, bioActive, type.noun) : null;
  if (!loaded) {
    promptShown.current = true;
    host.current = { content: { kind: "prompt", state: getModState<ZoneLoadState>(mod, "_zoneLoadState") }, display: "" };
    sibling.current = { ...sibling.current, display: "none" };
  } else {
    const target = promptShown.current ? "host" : "sibling";
    if (emptyText !== null) {
      if (target === "host") host.current = { content: { kind: "text", text: emptyText }, display: "" };
      else sibling.current = { text: emptyText, display: "" };
    } else if (target === "host") host.current = { ...host.current, display: "none" };
    else sibling.current = { ...sibling.current, display: "none" };
  }

  const onLoadClick = () => {
    setModState(mod, "_zoneLoadState", "loading");
    rerender();
    load().then(
      () => {
        setModState(mod, "_zoneLoadState", "loaded");
        rerender();
      },
      () => {
        setModState(mod, "_zoneLoadState", "error");
        rerender();
      },
    );
  };

  // The What chip opens the shared event value picker (same page-wide slot as the How
  // chips), pick-one: no exclude button.
  const [whatPicker, openWhat, closeWhat] = useSlotPicker<PopoverPos>("event", "value");
  const vis = loaded ? undefined : HIDDEN;
  const zoom = pitchZoomView(zone);

  const pitch = (
    <div className="event-pitch-wrap">
      <svg
        ref={svgRef}
        className={`event-pitch-svg event-pitch-svg-rotated${zoom.zoomed ? " event-zoomed" : ""}`}
        viewBox={zoom.viewBox}
        preserveAspectRatio="xMidYMid meet"
        // Inline only while zoomed, so the stylesheet's static 74:114 rule wins otherwise.
        style={zoom.aspectRatio ? { aspectRatio: zoom.aspectRatio } : undefined}
      >
        <defs className="event-defs"></defs>
        <g className="pitch-rotate" transform={zoom.transform}>
          <g className="pitch-frame">
            <PitchMarkings />
          </g>
          <g className="event-layer">
            {dots.dots.map((en, i) => (
              <circle key={i} cx={en.x} cy={en.y} r={dots.radius} className="zone-finder-dot"></circle>
            ))}
          </g>
          <g className="freeze-frame-layer"></g>
        </g>
      </svg>
      {zone && (
        <button type="button" className="event-area-reset-btn" onClick={() => setZone(null)}>
          Clear zone
        </button>
      )}
    </div>
  );

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainZoneFinder({
            noun: type.noun,
            league: ZONE_LEAGUE_OPTIONS[0].label,
            season: humanizeSeasonCode(ZONE_YEAR_CODE),
            zone: zone !== null,
            count: ranked.length,
          })}
        />
      )}
      <SubControls mod={mod} body={body}>
        <NameSearch
          visible={loaded}
          value={loaded ? nameSearch : ""}
          onInput={(v) => {
            setModState(mod, "_zoneNameSearch", v);
            rerender();
          }}
        />
        {/* Fixed, not real pickers — they only mirror Heat Map's strip. */}
        <StyledSelect items={LEAGUE_ITEMS} value="37" onChange={noop} wrapClass="zone-league-select-wrap" extraClass="event-competition-select" />
        <StyledSelect
          items={YEAR_ITEMS}
          value={ZONE_YEAR_CODE}
          onChange={noop}
          wrapClass="zone-year-select-wrap"
          extraClass="event-season-select"
        />
      </SubControls>
      <ZoneReadout mod={mod} body={body} count={loaded ? ranked.length : 0} zero={loaded ? ranked.length < 1 : false} />
      <div className="zone-finder-wrap">
        <EventDetailLayout pitch={pitch}>
          <div className="zone-finder-panel-content">
            <div className="event-load-prompt-host" style={loaded ? HIDDEN : undefined}>
              {promptShown.current && <HostBox content={host.current.content} display={host.current.display} onLoad={onLoadClick} />}
            </div>
            <div className="zone-finder-section-label zone-what-label" style={vis}>
              What
            </div>
            <div className="zone-what-chip-wrap" style={vis}>
              <div className="filter-chip zone-what-chip">
                <span
                  className="chip-value"
                  onClick={(e) => {
                    if (!loaded) return;
                    e.stopPropagation();
                    if (whatPicker) closeWhat();
                    else openWhat(popoverPosUnder(e.currentTarget));
                  }}
                >
                  {loaded ? type.label : ""}
                </span>
              </div>
            </div>
            <div className="zone-finder-section-label zone-who-label" style={vis}>
              Who
            </div>
            <div className="filter-bar zone-bio-filter-bar" style={vis}>
              {loaded ? (
                <FilterChipBar active={bioActive} refPlayer={refPlayer ?? NO_REF} options={filterOptions} rangeFilters={ZONE_RANGE_FILTERS} onChange={rerender} />
              ) : (
                <EmptyAddFilter />
              )}
            </div>
            <div className="zone-finder-section-label zone-how-label" style={vis}>
              How
            </div>
            {loaded ? (
              <EventFilterBar
                className="zone-event-filter-bar"
                active={eventActive}
                events={unionEvents}
                defs={type.filterDefs}
                onChange={rerender}
              />
            ) : (
              <div className="filter-bar event-filter-bar zone-event-filter-bar" style={HIDDEN}>
                <EmptyAddFilter />
              </div>
            )}
          </div>
        </EventDetailLayout>
        <div className="stats-empty" style={sibling.current.display === "none" ? HIDDEN : undefined}>
          {sibling.current.text}
        </div>
        <div className="ranking-list zone-finder-list">
          {emptyText === null &&
            ranked.map((r, i) => (
              <div key={r.player.name ?? i} className="ranking-row" onPointerDown={(e) => startRowDrag(e.nativeEvent, r.player)}>
                <span className="ranking-rank">{i + 1}</span>
                <span className="ranking-player">
                  <span className="ranking-player-name">{getPlayerShortName(r.player)}</span>
                  <span className="ranking-player-meta">{zoneRowMeta(r.player)}</span>
                </span>
                <span className="ranking-value">{formatCount(r.value)}</span>
              </div>
            ))}
        </div>
      </div>
      {whatPicker && loaded && (
        <ValuePicker
          label="What"
          pos={whatPicker}
          options={zoneWhatOptions()}
          activeValue={type.label}
          activeExcluded={false}
          excludable={false}
          onPick={(label) => {
            closeWhat();
            const next = zoneActionKeyForLabel(label);
            if (!next || next === typeKey) return;
            setModState(mod, "_zoneEventType", next);
            // A new action type has different How filters, so the old chips go.
            setModState(mod, "_zoneEventFilters", {});
            rerender();
          }}
          onClose={closeWhat}
        />
      )}
    </>
  );
}

/** A leaderboard row is a drag source (→ a Bio on empty canvas, or into a Radar/Stats) via
 *  startPlayerTokenDrag, as every other leaderboard row. Not on mobile widths (the
 *  legacy's 768px gate). */
function startRowDrag(e: PointerEvent, player: ZonePlayer) {
  if (window.innerWidth <= 768) return;
  e.preventDefault();
  e.stopPropagation();
  setStickyPlayer(playerIdOf(player));
  startPlayerTokenDrag(e);
}

/** The name search in the strip. Uncontrolled, with the value set each render, exactly as
 *  the legacy (`input.value = mod._zoneNameSearch`) — a controlled input would also write a
 *  `value` attribute the legacy never has. Keeps the `zone-name-search` class: Ben's
 *  page-wide select-all-on-click handler (#187) targets it. */
function NameSearch({ visible, value, onInput }: { visible: boolean; value: string; onInput: (v: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) inputRef.current.value = value;
  }, [value]);
  return (
    <div className="player-search-wrap zone-name-search-wrap" style={visible ? undefined : HIDDEN}>
      <input
        ref={inputRef}
        type="text"
        className="zone-name-search"
        placeholder="Search player name…"
        autoComplete="off"
        spellCheck={false}
        onInput={(e) => onInput(e.currentTarget.value)}
      />
      <button type="button" className="zone-name-search-clear" style={value ? undefined : HIDDEN} onClick={() => onInput("")}>
        ×
      </button>
    </div>
  );
}

/** The "N players loaded" box between the strip and the body — its own markup, not
 *  PopulationReadout's: the value/label sit in the same `.event-stat` block Heat Map's stats
 *  bar uses, so the two boxes match in height. No real per-90 exists for this season, so
 *  the toggle is kept but hidden, reading "Total" (as the legacy). The `zero` class is only
 *  touched once data is in; before that it reads a plain "0". */
function ZoneReadout({ mod, body, count, zero }: { mod: HTMLElement; body: HTMLElement; count: number; zero: boolean }) {
  const [el] = useState(() => {
    const d = document.createElement("div");
    d.className = "population-readout";
    return d;
  });
  useEffect(() => {
    // Mounted after <SubControls>, so the order stays [strip, readout, body].
    mod.insertBefore(el, body);
    return () => el.remove();
  }, [mod, body, el]);
  useEffect(() => {
    el.classList.toggle("zero", zero);
  }, [el, zero]);
  return createPortal(
    <>
      <div className="event-stat">
        <span className="event-stat-value">{count.toLocaleString()}</span>
        <span className="event-stat-label">players loaded</span>
      </div>
      <button className="value-mode-toggle" data-mode="per90" style={HIDDEN}>
        Total
      </button>
    </>,
    el,
  );
}

/** The load-prompt box's content: the prompt itself, or (the quirk) an empty message the
 *  legacy wrote over it. */
function HostBox({ content, display, onLoad }: { content: HostContent; display: "" | "none"; onLoad: () => void }) {
  let inner: ReactNode;
  if (content.kind === "text") inner = content.text;
  else {
    const prompt = zoneLoadPrompt(content.state);
    inner = (
      <>
        <p className="event-load-prompt-experimental">{ZONE_EXPERIMENTAL_TEXT}</p>
        <p className="event-load-prompt-text">{prompt.text}</p>
        {prompt.button && (
          <button type="button" className="event-load-prompt-btn" onClick={onLoad}>
            {prompt.button}
          </button>
        )}
      </>
    );
  }
  return (
    <div className="stats-empty event-load-prompt" style={display === "none" ? HIDDEN : undefined}>
      {inner}
    </div>
  );
}

/** The Who / How bars before the data is in: just the (hidden) add-filter button with an
 *  empty menu — the legacy only fills them once loaded. */
function EmptyAddFilter() {
  return (
    <div className="add-filter-wrap">
      <button className="add-filter-btn">+ Add filter</button>
      <div className="add-filter-menu"></div>
    </div>
  );
}
